import type {
  FetchUsersParams,
  FetchUsersResponse,
  User,
  UserGroup,
} from '@e-learning/shared';
import type { RequestConfig } from '../types';

/** Payload para registro de usuário (POST v1/User). */
export interface RegisterUserRequest {
  name: string;
  email: string;
  roleIds: string[];
  userGroupIds: string[];
}

/** Payload para edição de usuário (PUT v1/User/{id}). id vai na URL; demais campos no body. */
export interface UpdateUserRequest {
  /** GUID do usuário (enviado na URL). */
  id: string;
  isActive: boolean;
  name: string;
  email: string;
  /** IDs das roles (GUIDs). */
  roleIds: string[];
  /** IDs dos grupos de usuário (GUIDs). */
  userGroupIds: string[];
}

/** Cliente mínimo necessário para as chamadas (get, post, put). Compatível com ApiClient do core. */
export interface UserApiClient {
  get<T>(url: string, config?: RequestConfig): Promise<T>;
  post<T>(url: string, data?: unknown, config?: unknown): Promise<T>;
  put<T>(url: string, data?: unknown, config?: unknown): Promise<T>;
}

export type FetchUsersFn = (
  params: FetchUsersParams
) => Promise<FetchUsersResponse>;

/** Grupo de utilizador associado na resposta GET v1/User/{id}. */
export interface UserDetailUserGroup {
  id: string;
  name: string;
}

/** Corpo JSON de GET v1/User/{id} (text/plain ou application/json). */
export interface UserDetailResponse {
  id: string;
  name: string;
  email: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  lastLoginAt: string | null;
  roleIds: string[];
  userGroups: UserDetailUserGroup[];
}

/** Item de usuário retornado pela API v1/User/pagination */
interface UserPaginationApiItem {
  id: string;
  name: string | null;
  email: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  lastLoginAt: string;
  roles: Array<{ id: string; name: string }>;
  userGroups: Array<{ id: string; name?: string }>;
}

interface UserPaginationApiResponse {
  users: UserPaginationApiItem[];
  totalRecords: number;
  totalPages: number;
  pageNumber: number;
  pageSize: number;
}

/** Resposta bruta da API (aceita formato v1 ou legado data/items). */
type RawUsersResponse =
  | UserPaginationApiResponse
  | {
      data?: User[];
      items?: User[];
      total?: number;
      totalCount?: number;
    };

const USERS_PAGINATION_PATH = 'v1/User/pagination';
const USER_PATH = 'v1/User';
const USER_GROUP_PAGINATION_PATH = 'v1/UserGroup/pagination';

/** Item de grupo retornado pela API v1/UserGroup/pagination */
interface UserGroupPaginationApiItem {
  id: string;
  name: string;
  assignedCourses: number;
  qtdUsers: number;
}

interface UserGroupPaginationApiResponse {
  userGroups: UserGroupPaginationApiItem[];
  totalRecords: number;
  totalPages: number;
  pageNumber: number;
  pageSize: number;
}

export interface FetchUserGroupsParams {
  page: number;
  limit: number;
  /** Filtro por nome do grupo (Name na API) */
  search?: string;
  /** Incluir grupos inativos (includeInactive na API). Default: false */
  includeInactive?: boolean;
}

export interface FetchUserGroupsResponse {
  data: UserGroup[];
  total: number;
}

/** Verifica se a resposta é do formato v1/User/pagination */
function isUserPaginationResponse(
  raw: RawUsersResponse
): raw is UserPaginationApiResponse {
  return (
    'users' in raw &&
    Array.isArray((raw as UserPaginationApiResponse).users) &&
    typeof (raw as UserPaginationApiResponse).totalRecords === 'number'
  );
}

/** Mapeia item de grupo da API v1 para o tipo UserGroup do shared */
function mapApiUserGroupToUserGroup(
  item: UserGroupPaginationApiItem
): UserGroup {
  return {
    id: item.id,
    name: item.name,
  };
}

/** Mapeia item da API v1 para o tipo User do shared */
function mapApiUserToUser(item: UserPaginationApiItem): User {
  const mapped = {
    id: item.id,
    name: item.name ?? '',
    email: item.email,
    isActive: item.isActive,
    createdAt: item.createdAt,
    updatedAt: item.updatedAt,
    lastLoginAt: item.lastLoginAt,
    roles: item.roles ?? [],
    userGroups: item.userGroups ?? [],
    status: item.isActive ? 'Active' : 'Inactive',
    joinDate: item.createdAt ? item.createdAt.slice(0, 10) : undefined,
  };
  return mapped as unknown as User;
}

/**
 * Normaliza a resposta da API para { data, total }.
 * Suporta formato v1/User/pagination (users, totalRecords) e legado (data/items, total).
 */
function normalizeUsersResponse(raw: RawUsersResponse): FetchUsersResponse {
  if (isUserPaginationResponse(raw)) {
    return {
      data: raw.users.map(item => mapApiUserToUser(item)),
      total: raw.totalRecords,
    };
  }
  const data = raw.data ?? raw.items ?? [];
  const total =
    raw.total ?? raw.totalCount ?? (Array.isArray(data) ? data.length : 0);
  return {
    data: Array.isArray(data) ? data : [],
    total,
  };
}

/**
 * Cria o cliente de API de gestão de usuários.
 * Recebe o api do app (b2b/b2b2c) e retorna { fetchUsers, registerUser, updateUser, inactivateUser }.
 */
export function createUserApi(api: UserApiClient) {
  return {
    async fetchUsers(params: FetchUsersParams): Promise<FetchUsersResponse> {
      const { page, limit, search } = params;
      const searchParams = new URLSearchParams();
      searchParams.set('pageNumber', String(page));
      searchParams.set('pageSize', String(limit));
      if (search) searchParams.set('SearchText', search);

      const query = searchParams.toString();
      const url = query
        ? `/${USERS_PAGINATION_PATH}?${query}`
        : `/${USERS_PAGINATION_PATH}`;

      try {
        const raw = await api.get<RawUsersResponse>(url);
        return normalizeUsersResponse(raw);
      } catch {
        return { data: [], total: 0 };
      }
    },

    /**
     * Lista grupos de usuário (GET v1/UserGroup/pagination).
     * Usa Name como filtro de busca e paginação server-side.
     */
    async fetchUserGroups(
      params: FetchUserGroupsParams
    ): Promise<FetchUserGroupsResponse> {
      const { page, limit, search, includeInactive = false } = params;
      const searchParams = new URLSearchParams();
      searchParams.set('PageNumber', String(page));
      searchParams.set('PageSize', String(limit));
      searchParams.set('includeInactive', String(includeInactive));
      if (search) searchParams.set('Name', search);

      const query = searchParams.toString();
      const url = query
        ? `/${USER_GROUP_PAGINATION_PATH}?${query}`
        : `/${USER_GROUP_PAGINATION_PATH}`;

      try {
        const raw = await api.get<UserGroupPaginationApiResponse>(url);
        const data = Array.isArray(raw.userGroups) ? raw.userGroups : [];
        return {
          data: data.map(mapApiUserGroupToUserGroup),
          total: typeof raw.totalRecords === 'number' ? raw.totalRecords : 0,
        };
      } catch {
        return { data: [], total: 0 };
      }
    },

    /** Registra um novo usuário (POST v1/User). */
    async registerUser(request: RegisterUserRequest): Promise<void> {
      await api.post(`/${USER_PATH}`, request);
    },

    /** Atualiza um usuário existente (PUT v1/User/{id}). Id na URL; body: isActive, name, email, roleIds, userGroupIds. */
    async updateUser(request: UpdateUserRequest): Promise<void> {
      const { id, ...body } = request;
      await api.put(`/${USER_PATH}/${encodeURIComponent(id)}`, body);
    },

    /** Inativa um usuário (PUT v1/User/inactive/{id}). Equivalente a "deletar" na API. */
    async inactivateUser(id: string): Promise<void> {
      await api.put(`/${USER_PATH}/inactive/${encodeURIComponent(id)}`);
    },

    /**
     * Detalhe do utilizador (GET v1/User/{id}).
     * Headers: Accept-Language (via cliente), Accept: text/plain (conforme API).
     */
    async fetchUserById(userId: string): Promise<UserDetailResponse | null> {
      const id = userId?.trim();
      if (!id) return null;
      try {
        return await api.get<UserDetailResponse>(
          `/${USER_PATH}/${encodeURIComponent(id)}`,
          {
            headers: {
              Accept: 'text/plain',
            },
          }
        );
      } catch {
        return null;
      }
    },
  };
}

export type UserApi = ReturnType<typeof createUserApi>;
